import { FilePdfOutlined, FileTextOutlined, GlobalOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import * as S from './Attachment.styles';
import * as D from '../../DealRoomLanding.styles';
import { IDealRoomInfoFile } from '@app/interfaces/deal-room-info';
import { IFile } from '@app/interfaces/file';
import { getExtension, getReadableFileName, getTabId, getUserFullName, openLinkInNewTab } from '@app/utils/utils';
import { Tooltip } from 'antd';
import {
  deleteDealRoomInfoFile,
  deleteExternalDealRoomInfoFile,
  downloadDealRoomInfoFile,
} from '@app/api/deal-room-info.api';
import { IDealRoomShare } from '@app/interfaces/deal-room';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { notificationController } from '@app/controllers/notificationController';
import { useTranslation } from 'react-i18next';
import { IUser } from '@app/interfaces/user';
import { IDealRoomPeople } from '@app/interfaces/deal-room-people';
import { DeleteIcon } from '@app/components/common/icons/DeleteIcon';
import { Popconfirm } from '@app/components/common/Popconfirm/Popconfirm';
import { DealRoomStatus } from '@app/constants/enums/deal-room';
import { openFile } from '@app/utils/file';

interface AttachmentProps {
  dealRoom?: IDealRoomShare;
  internalFiles?: IDealRoomInfoFile[];
  externalFiles?: IDealRoomInfoFile[];
  people?: IDealRoomPeople | null;
  onDeleteSuccess?: (isExternal: boolean) => void;
}

export const Attachment: React.FC<AttachmentProps> = ({
  dealRoom,
  internalFiles,
  externalFiles,
  people,
  onDeleteSuccess,
}) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user.user);
  const isAdmin = useAppSelector((state) => state.user.isAdmin);
  const [count, setCount] = useState(0);
  const [loader, setLoader] = useState(false);
  const [isDealRoomClosed, setIsDealRoomClosed] = useState(false);

  const getIcon = (data: IDealRoomInfoFile) => {
    if (data.link) return <GlobalOutlined />;
    const extension = getExtension((data.file as IFile)?.originalName || '')?.toLowerCase();
    if (extension === 'pdf') return <FilePdfOutlined />;
    return <FileTextOutlined />;
  };

  const downloadFile = async (file: IDealRoomInfoFile) => {
    setLoader(true);
    try {
      const newWindow = file.link ? window.open('', '_blank') : null;
      if (file.link) {
        openLinkInNewTab(newWindow, file.link);
      } else {
        const data = await downloadDealRoomInfoFile(dealRoom?.id as string, file._id as string, {
          sessionId: dealRoom?.sessionId,
          uniqueId: dealRoom?.uniqueId,
          userId: user?.id,
        });
        openFile(data.id);
      }
    } catch (error) {
      notificationController.error({
        message: t('dealRooms.form.errorDownloadingFile'),
      });
    } finally {
      setLoader(false);
    }
  };

  const onDelete = async (file: IDealRoomInfoFile) => {
    setLoader(true);
    try {
      user
        ? await deleteDealRoomInfoFile(dealRoom?.id as string, file?._id as string, getTabId())
        : await deleteExternalDealRoomInfoFile(
            dealRoom?.id as string,
            file?._id as string,
            people?.id as string,
            getTabId(),
          );
      if (onDeleteSuccess) onDeleteSuccess(!!file.isExternal);
      notificationController.success({ message: t('dealRooms.form.documentDeletedSuccessfully') });
    } catch (error) {
      notificationController.error({
        message: (error as Error).message,
      });
    } finally {
      setLoader(false);
    }
  };

  const getDescription = (file: IDealRoomInfoFile) => {
    const user = file.user
      ? getUserFullName(file.user as IUser)
      : file.dealRoomPeople
      ? `${(file.dealRoomPeople as IDealRoomPeople)?.firstName} ${
          (file.dealRoomPeople as IDealRoomPeople)?.lastName || ''
        }`
      : '';
    return `${file.description ? file.description : ''}${user ? ` | ${user}` : ''}`;
  };

  useEffect(() => {
    const internalCount = internalFiles ? internalFiles.length : 0;
    const externalCount = externalFiles ? externalFiles.length : 0;
    setCount(internalCount + externalCount);
  }, [internalFiles, externalFiles]);

  useEffect(() => {
    setIsDealRoomClosed(dealRoom?.status === DealRoomStatus.CLOSED);
  }, [dealRoom?.status]);

  return (
    <>
      {count > 0 ? (
        <Spinner spinning={loader}>
          <S.Wrapper>
            <S.Title>קבצים מצורפים ({count})</S.Title>
            <S.ListWrapper>
              {internalFiles && internalFiles?.length > 0 && (
                <>
                  {internalFiles.map((file) => (
                    <Tooltip key={file._id} title={getDescription(file)}>
                      <S.AttachmentButton icon={getIcon(file)} onClick={() => downloadFile(file)}>
                        {file.link ? file.link : getReadableFileName((file.file as IFile)?.originalName || '')}
                        {user &&
                          !isDealRoomClosed &&
                          (user.id === (file.user as IUser)?.id ||
                            isAdmin ||
                            (dealRoom?.responsible as IUser)?.id === user?.id) && (
                            <Popconfirm
                              title={t('dealRooms.table.deleteInfo')}
                              okText={t('common.yes')}
                              cancelText={t('common.no')}
                              onConfirm={(e) => {
                                e?.stopPropagation();
                                onDelete(file);
                              }}
                              onCancel={(e) => e?.stopPropagation()}
                            >
                              <DeleteIcon
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              />
                            </Popconfirm>
                          )}
                      </S.AttachmentButton>
                    </Tooltip>
                  ))}
                </>
              )}
              {externalFiles && externalFiles?.length > 0 && (
                <>
                  {externalFiles.map((file) => (
                    <Tooltip key={file._id} title={getDescription(file)}>
                      <S.AttachmentButton icon={getIcon(file)} onClick={() => downloadFile(file)}>
                        {file.link ? file.link : getReadableFileName((file.file as IFile)?.originalName || '')}
                        {!isDealRoomClosed &&
                          ((people && people.id === (file.dealRoomPeople as IDealRoomPeople)?.id) ||
                            isAdmin ||
                            (dealRoom?.responsible as IUser)?.id === user?.id) && (
                            <Popconfirm
                              title={t('dealRooms.table.deleteInfo')}
                              okText={t('common.yes')}
                              cancelText={t('common.no')}
                              onConfirm={(e) => {
                                e?.stopPropagation();
                                onDelete(file);
                              }}
                              onCancel={(e) => e?.stopPropagation()}
                            >
                              <DeleteIcon
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                              />
                            </Popconfirm>
                          )}
                      </S.AttachmentButton>
                    </Tooltip>
                  ))}
                </>
              )}
            </S.ListWrapper>
          </S.Wrapper>
        </Spinner>
      ) : (
        <D.NoData>{t('dealRooms.landing.noAttachments')}</D.NoData>
      )}
    </>
  );
};
